import type { RentalInvoiceView, RentalLifecycleFull, RentalLineView, RentalSaleLineView } from '@app/sales/rental-billing/rental-billing.model';
import { frDay } from '@app/sales/rental-billing/rental-days';
import { patientLabel, rentalSubtitle, rentalTitle } from '@app/sales/rental-billing/rental-billing.labels';
import { businessDayOf } from '@app/shared/time';
import { normalizeSearch } from '@app/hr/hr-leave-board';

// Valeur brute du formulaire de la pièce (getRawValue) : seuls les champs lus par la vue ; l'état de facturation vient du serveur (/lifecycle).
export interface LifecycleIn { qty?: number | null; return_realdatdel?: string | null; }
export interface TermsIn { id?: number | null; name?: string | null; period?: number | null; vat_rate?: number | string | null; tiered_pricing?: { uprice_taxx?: number; starting_period?: number }[] | null; data?: any; }
export interface LineIn {
  id?: number | null; seq?: number | null; typ?: string | null; product?: number | null; product_name?: string | null; qty?: number | null; memo?: string | null; data?: any;
  vat_rate?: number | string | null; unet_taxx?: number | null; unet_taxi?: number | null; net_taxx?: number | null; net_taxi?: number | null;
  rental_terms_snapshot?: TermsIn | null; rental_lifecycle?: LifecycleIn | null;
}
export interface DeliveryLineIn { id_order_l?: number | string | null; qty?: number | string | null; }
export interface DeliveryIn { id?: number | null; mode?: number | string | null; askdatdel?: string | null; realdatdel?: string | null; lnk_deliveryl?: DeliveryLineIn[] | null; }
export interface OrderIn { lines?: LineIn[] | null; lnk_deliveries?: DeliveryIn[] | null; }

// Faits de BL seulement (pose = quantité positive, reprise = −1 sur la même ligne, JP 29/10) : tuiles, Récupérer et Planifier ; le statut de facturation est celui du serveur.
export type RowState = 'to-plan' | 'planned' | 'in-place' | 'return-planned' | 'returned';
export type Bucket = 'to-deliver' | 'in-place' | 'to-collect' | 'returned';
// Trois niveaux d'un matériel (JP 28/10) : chez le patient ou à livrer ; repris mais pas soldé ; repris et soldé (statut serveur `closed`, sans anomalie).
export type Level = 'current' | 'unbilled' | 'closed';

// `dates` : écart de dates que l'utilisateur peut déclarer juste (data.dates_verifiees) ; `server` : anomalie rendue par /lifecycle.
export interface Anomaly { label: string; detail: string; dates?: boolean; server?: boolean; blocked?: boolean; }

// Trace « les dates sont bonnes » posée sur la ligne RENTAL ; elle ne vaut que pour ces deux jours-là.
export interface DatesChecked { livre: string | null; repris: string | null; par?: number | null; nom?: string | null; le?: string | null; }

// Filtres d'affichage : niveaux cochés ; période Du / Au sur le jour de livraison, jours aaaa-mm-jj inclus ou null (borne ouverte).
export interface RowFilter { levels: Record<Level, boolean>; start: string | null; end: string | null; }

// Une rangée = une ligne RENTAL ; `index` est sa place dans le FormArray, seule clé pour la modifier.
export interface RentalRow {
  index: number;
  id: number;
  seq: number;
  isNew: boolean;
  product: number | null;
  patient: string;
  productName: string;
  installedOn: string | null;      // BL local : plus petit realdatdel, jour de Paris aaaa-mm-jj
  planned: boolean;                // BL portant la ligne, pas encore signé
  plannedOn: string | null;
  plannedBl: number | null;        // id de ce BL non signé : lien « prévue le »
  returnDoneOn: string | null;     // BL de reprise signé : la ligne y est à −1
  returnPlanned: boolean;
  returnPlannedOn: string | null;
  returnPlannedBl: number | null;
  returnedOn: string | null;       // rental_lifecycle.return_realdatdel, écrit à la signature du BL de reprise
  server: RentalLineView | null;   // ligne de /lifecycle (même id), null avant son arrivée
  deliveredOn: string | null;      // « Livré le », jour : jour de Paris de server.realdatdel (instant du BL), sinon BL local
  backOn: string | null;           // « Repris le », jour : jour de Paris de server.return_realdatdel, sinon rental_lifecycle.return_realdatdel
  datesChecked: DatesChecked | null;
  state: RowState;
  bucket: Bucket;
  anomalies: Anomaly[];
  level: Level;                    // `unbilled` tant que la vue serveur d'un matériel repris n'est pas arrivée
  canCollect: boolean;
}

export interface RentalGroup {
  key: string;
  title: string;                   // intitulé du contrat de location, l'article sans contrat
  article: string | null;          // l'article sous l'intitulé du contrat ; null sans contrat
  termsName: string;
  periodLabel: string;             // nom c_values 16 en minuscules, « période » à défaut
  vatRate: number;
  unitTaxx: number;
  unitTaxi: number;
  rows: RentalRow[];
}

// Reprise hors contrat (JP 30/10) : ligne louée à quantité négative (data.hors_contrat), seul son BL −n existe ; `qty` = quantité reprise.
export interface OutsideReturn {
  index: number; id: number; seq: number; product: number | null; patient: string; productName: string; qty: number;
  doneOn: string | null; planned: boolean; plannedOn: string | null; plannedBl: number | null;
}

export interface OtherLine {
  index: number; id: number; seq: number; typ: string; name: string; qty: number;
  unitTaxx: number; unitTaxi: number; netTaxx: number; netTaxi: number;
  sale: RentalSaleLineView | null; // ligne de /lifecycle.sales (même id)
}

export interface HeaderProblem { row: RentalRow; who: string; what: string; blocked: boolean; rx: boolean; }

export interface RentalView {
  rows: RentalRow[];
  groups: RentalGroup[];
  outside: OutsideReturn[];        // reprises hors contrat : voulues sans matériel suivi, jamais une anomalie
  others: OtherLine[];
  counts: Record<Level, number>;
  tiles: { inPlace: number; toDeliver: number; toCollect: number; returned: number; returnedWithAnomaly: number };
  anomalies: number;
}

export const EMPTY_VIEW: RentalView = {
  rows: [], groups: [], outside: [], others: [], counts: { current: 0, unbilled: 0, closed: 0 },
  tiles: { inPlace: 0, toDeliver: 0, toCollect: 0, returned: 0, returnedWithAnomaly: 0 }, anomalies: 0,
};

const BUCKETS: Record<RowState, Bucket> = {
  'to-plan': 'to-deliver', 'planned': 'to-deliver', 'in-place': 'in-place',
  'return-planned': 'to-collect', 'returned': 'returned',
};

// Jour de Paris d'un instant du serveur ; « +02 » tronqué par un cast ::text complété avant la lecture par shared/time.
// Une date nue (colonne DATE, askdatdel) est déjà un jour : la lire comme un instant la décalerait selon le fuseau du poste.
export function parisDay(v: unknown): string | null {
  if (v == null || v === '') return null;
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return businessDayOf(/\d{2}:\d{2}(:\d{2}(\.\d+)?)?[+-]\d{2}$/.test(s) ? s + ':00' : s);
}

// La validation ne couvre que les jours validés : une date corrigée ensuite fait revenir l'avertissement.
export function datesChecked(raw: any, livre: string | null, repris: string | null): DatesChecked | null {
  if (!raw || typeof raw !== 'object') return null;
  return (raw.livre ?? null) === livre && (raw.repris ?? null) === repris ? raw as DatesChecked : null;
}

function lineId(v: unknown): number | null {
  const n = Number(v);
  return v !== null && v !== '' && Number.isInteger(n) && n > 0 ? n : null;
}

const isRental = (l: LineIn) => l?.typ === 'RENTAL';

// Reprise d'un matériel qu'aucun dossier ne suit (JP 30/10) : ligne louée à quantité négative, marquée data.hors_contrat, jamais facturée.
export const isOutOfContract = (l: LineIn) => isRental(l) && l?.data?.hors_contrat === true;

interface Moves { done: string[]; pending: { day: string | null; id: number }[]; }
interface LineDeliveries { out: Moves; back: Moves; }

// BL d'une ligne : mode >= 0 (un bon d'annulation a un mode négatif) ; quantité > 0 = pose, < 0 = reprise sur la même ligne (règle de JP du 29/10).
function deliveriesByLine(deliveries: DeliveryIn[]): Map<number, LineDeliveries> {
  const map = new Map<number, LineDeliveries>();
  for (const d of deliveries) {
    if (!(Number(d?.mode) >= 0)) continue;
    const done = parisDay(d.realdatdel);
    for (const dl of d.lnk_deliveryl ?? []) {
      const id = lineId(dl?.id_order_l);
      const q = Number(dl?.qty);
      if (id === null || !q) continue;
      const e = map.get(id) ?? { out: { done: [], pending: [] }, back: { done: [], pending: [] } };
      const m = q > 0 ? e.out : e.back;
      if (d.realdatdel && done) m.done.push(done);
      else if (!d.realdatdel) m.pending.push({ day: parisDay(d.askdatdel), id: Number(d.id) || 0 });
      map.set(id, e);
    }
  }
  return map;
}

function firstDay(days: (string | null)[]): string | null {
  return days.filter((d): d is string => !!d).sort()[0] ?? null;
}

interface LineDelivery { doneOn: string | null; planned: boolean; plannedOn: string | null; plannedBl: number | null; }
const NO_MOVE: LineDelivery = { doneOn: null, planned: false, plannedOn: null, plannedBl: null };

// BL non signé montré : le plus tôt demandé, un BL sans date en dernier (la garde serveur n'en laisse qu'un par sens et par ligne).
function moveOf(m: Moves | undefined): LineDelivery {
  if (!m) return NO_MOVE;
  const doneOn = firstDay(m.done);
  const next = !doneOn ? [...m.pending].sort((a, b) => (a.day ?? '￿').localeCompare(b.day ?? '￿'))[0] : undefined;
  return { doneOn, planned: !!next, plannedOn: next?.day ?? null, plannedBl: next?.id || null };
}

// Jointure de la pièce (BL de pose et de reprise de chaque ligne louée) et de /lifecycle par id de ligne ; `lifecycle` null tant que la vue n'est pas arrivée.
export function buildRentalView(order: OrderIn | null | undefined, periodNames: Record<number, string>, lifecycle: RentalLifecycleFull | null): RentalView {
  const lines = order?.lines ?? [];
  const bl = deliveriesByLine(order?.lnk_deliveries ?? []);
  const serverLines = new Map<number, RentalLineView>((lifecycle?.lines ?? []).map((l) => [l.id, l]));
  const serverSales = new Map<number, RentalSaleLineView>((lifecycle?.sales ?? []).map((s) => [s.id, s]));
  const rows: RentalRow[] = [];

  lines.forEach((l, index) => {
    if (!isRental(l) || isOutOfContract(l)) return;
    const id = Number(l.id) || 0;
    const lc = l.rental_lifecycle ?? {};
    const anomalies: Anomaly[] = [];

    const moves = id > 0 ? bl.get(id) : undefined;
    const install = moveOf(moves?.out);
    const ret = moveOf(moves?.back);
    const returnedOn = parisDay(lc.return_realdatdel);
    const server = id > 0 ? serverLines.get(id) ?? null : null;
    const deliveredOn = parisDay(server?.realdatdel) ?? install.doneOn;
    const backOn = parisDay(server?.return_realdatdel) ?? returnedOn;

    const checked = datesChecked(l.data?.dates_verifiees, install.doneOn, returnedOn);
    if (returnedOn && !install.doneOn) anomalies.push({ label: 'Repris sans livraison', detail: `Restitué le ${frDay(returnedOn)} sans livraison signée sur ce matériel` });
    else if (returnedOn && install.doneOn && returnedOn < install.doneOn && !checked) {
      anomalies.push({ label: 'Dates à vérifier', detail: `Repris le ${frDay(returnedOn)}, avant sa livraison du ${frDay(install.doneOn)}`, dates: true });
    }
    for (const msg of server?.anomalies ?? []) anomalies.push({ label: msg, detail: msg, server: true, blocked: server!.blocked });

    // Un BL −1 signé sans return_realdatdel est un retour : le moteur le lit sur ce BL et la vue serveur le dit.
    let state: RowState;
    if (backOn || ret.doneOn) state = 'returned';
    else if (!deliveredOn) state = install.planned ? 'planned' : 'to-plan';
    else if (ret.planned) state = 'return-planned';
    else state = 'in-place';

    rows.push({
      index, id, seq: Number(l.seq) || 0, isNew: id === 0, product: lineId(l.product),
      patient: (l.memo ?? '').trim(), productName: l.product_name ?? '',
      installedOn: install.doneOn, planned: install.planned, plannedOn: install.plannedOn, plannedBl: install.plannedBl,
      returnDoneOn: ret.doneOn, returnPlanned: ret.planned, returnPlannedOn: ret.plannedOn, returnPlannedBl: ret.plannedBl,
      returnedOn, server, deliveredOn, backOn, datesChecked: checked, state, bucket: BUCKETS[state],
      // Reprise programmable dès la pose planifiée (JP 04/11) ; le serveur en refuse la signature tant que la pose n'est pas signée.
      anomalies, level: 'current', canCollect: id > 0 && (state === 'in-place' || (state === 'planned' && !ret.planned)),
    });
  });

  // Un matériel encore sur place reste « en cours » même si sa facturation est close (fin décidée sans reprise) ; une anomalie n'est jamais close.
  for (const r of rows) r.level = r.state !== 'returned' ? 'current' : r.server?.status === 'closed' && !r.anomalies.length ? 'closed' : 'unbilled';

  const outside: OutsideReturn[] = [];
  lines.forEach((l, index) => {
    if (!isOutOfContract(l)) return;
    const id = Number(l.id) || 0;
    const d = moveOf(id > 0 ? bl.get(id)?.back : undefined);
    outside.push({ index, id, seq: Number(l.seq) || 0, product: lineId(l.product), patient: (l.memo ?? '').trim(), productName: l.product_name ?? '', qty: Math.abs(Number(l.qty) || 0), ...d });
  });

  // Ancienne ligne RENTAL_RETURN (type plus créé) : ni matériel ni vente, jamais facturée.
  const others: OtherLine[] = [];
  lines.forEach((l, index) => {
    if (isRental(l) || l?.typ === 'RENTAL_RETURN') return;
    const id = Number(l.id) || 0;
    others.push({
      index, id, seq: Number(l.seq) || 0, typ: String(l.typ ?? ''), name: l.product_name ?? '', qty: Number(l.qty) || 0,
      unitTaxx: Number(l.unet_taxx) || 0, unitTaxi: Number(l.unet_taxi) || 0, netTaxx: Number(l.net_taxx) || 0, netTaxi: Number(l.net_taxi) || 0,
      sale: id > 0 ? serverSales.get(id) ?? null : null,
    });
  });

  const groups = new Map<string, RentalGroup>();
  for (const r of [...rows].sort((a, b) => a.seq - b.seq)) {
    const l = lines[r.index];
    const t = l.rental_terms_snapshot ?? {};
    const key = [l.product, t.id, l.product_name, Number(l.unet_taxx), Number(l.unet_taxi), Number(l.vat_rate)].join('|');
    let g = groups.get(key);
    if (!g) {
      const period = t.period != null ? periodNames[Number(t.period)] : undefined;
      const named = { terms_name: (t.name ?? '').trim(), product_name: l.product_name };
      g = { key, title: rentalTitle(named) ?? '', article: rentalSubtitle(named), termsName: named.terms_name, periodLabel: period?.toLowerCase() || 'période', vatRate: Number(l.vat_rate) || 0, unitTaxx: Number(l.unet_taxx) || 0, unitTaxi: Number(l.unet_taxi) || 0, rows: [] };
      groups.set(key, g);
    }
    g.rows.push(r);
  }

  const returned = rows.filter((r) => r.state === 'returned');
  return {
    rows,
    groups: [...groups.values()],
    outside: outside.sort((a, b) => a.seq - b.seq),
    others: others.sort((a, b) => a.seq - b.seq),
    counts: countLevels(rows),
    tiles: {
      inPlace: rows.filter((r) => r.bucket === 'in-place').length,
      toDeliver: rows.filter((r) => r.bucket === 'to-deliver').length,
      toCollect: rows.filter((r) => r.bucket === 'to-collect').length,
      returned: returned.length,
      returnedWithAnomaly: returned.filter((r) => r.anomalies.length > 0).length,
    },
    anomalies: rows.filter((r) => r.anomalies.length > 0).length,
  };
}

function countLevels(rows: RentalRow[]): Record<Level, number> {
  const n: Record<Level, number> = { current: 0, unbilled: 0, closed: 0 };
  for (const r of rows) n[r.level]++;
  return n;
}

// Jour de livraison (ou jour prévu) entre start et end inclus, null = sans borne (JP 28/10 : la période porte sur la livraison) ; sans date, toujours montré.
export function inPeriod(r: RentalRow, filterStart: string | null, filterEnd: string | null): boolean {
  const day = r.deliveredOn ?? r.plannedOn;
  if (!day) return true;
  return (!filterStart || day >= filterStart) && (!filterEnd || day <= filterEnd);
}

// Une ligne neuve reste toujours visible.
export function rowVisible(r: RentalRow, f: RowFilter): boolean {
  return r.isNew || (f.levels[r.level] && inPeriod(r, f.start, f.end));
}

export function visibleGroups(view: RentalView, f: RowFilter): RentalGroup[] {
  return view.groups.map((g) => ({ ...g, rows: g.rows.filter((r) => rowVisible(r, f)) })).filter((g) => g.rows.length > 0);
}

export function hiddenCount(view: RentalView, f: RowFilter): number {
  return view.rows.filter((r) => !rowVisible(r, f)).length;
}

// Lignes enregistrées retenues par les filtres : celles que montre le Planning embarqué.
export function visibleLineIds(view: RentalView, f: RowFilter): number[] {
  return view.rows.filter((r) => r.id > 0 && rowVisible(r, f)).map((r) => r.id).sort((a, b) => a - b);
}

// Compteur de chaque case après la période : ce qu'elle montrerait cochée (une ligne neuve compte « en cours »).
export function levelCounts(view: RentalView, f: RowFilter): Record<Level, number> {
  return countLevels(view.rows.filter((r) => r.isNew || inPeriod(r, f.start, f.end)));
}

// Points à vérifier de l'en-tête, dits en clair : un par anomalie de rangée (patient, matériel, motif).
// `rx` : ordonnance manquante ou expirée, d'après les deux avertissements du moteur (« prescription manquante », « prescription expirée le … »).
export function headerProblems(view: RentalView, beneficiary: string | null = null): HeaderProblem[] {
  const who = (patient: string, product: string) => `${patientLabel(patient, beneficiary)} · ${product}`;
  return view.rows.flatMap((r) => r.anomalies.map((a): HeaderProblem => ({
    row: r, who: who(r.patient, r.productName), what: a.server ? a.detail : `${a.label} : ${a.detail}`, blocked: !!a.blocked,
    rx: !!a.server && !!r.server?.params?.prescription_required && a.detail.startsWith('prescription '),
  })));
}

// Patients encore là (en place, à livrer, à reprendre), un par mémo au sens du Planning (casse et accents ignorés), graphie de la première ligne gardée.
export interface PresentPatient { memo: string; key: string; products: string[]; }
export function presentPatients(view: RentalView): PresentPatient[] {
  const byKey = new Map<string, PresentPatient>();
  for (const r of view.rows) {
    if (r.state === 'returned' || !r.patient) continue;
    const key = normalizeSearch(r.patient);
    const p = byKey.get(key) ?? { memo: r.patient, key, products: [] };
    p.products.push(r.productName);
    byKey.set(key, p);
  }
  return [...byKey.values()].sort((a, b) => a.memo.localeCompare(b.memo, 'fr', { numeric: true, sensitivity: 'base' }));
}

export function matchingPatients(list: PresentPatient[], text: string): PresentPatient[] {
  const q = normalizeSearch(text);
  return q ? list.filter((p) => p.key.includes(q)) : list;
}

// Saisie qui tombe sur la rangée d'un patient présent ; `exact` faux = même rangée au Planning mais orthographe différente sur le BL.
export function samePatient(list: PresentPatient[], text: string): { patient: PresentPatient; exact: boolean } | null {
  const q = normalizeSearch(text);
  const patient = q ? list.find((p) => p.key === q) : undefined;
  return patient ? { patient, exact: patient.memo === text.trim() } : null;
}

// Factures et avoirs du dossier, le plus récent en haut (jour de scellement, puis id) et par année ; une pièce sans jour se range en tête, seule.
export interface InvoiceYear { year: string | null; invoices: RentalInvoiceView[]; }
export function invoiceYears(invoices: RentalInvoiceView[]): InvoiceYear[] {
  const sorted = [...invoices].sort((a, b) => (b.invoiced_on ?? '9999').localeCompare(a.invoiced_on ?? '9999') || b.id - a.id);
  const years: InvoiceYear[] = [];
  for (const inv of sorted) {
    const year = inv.invoiced_on?.slice(0, 4) ?? null;
    const last = years.at(-1);
    if (last && last.year === year) last.invoices.push(inv);
    else years.push({ year, invoices: [inv] });
  }
  return years;
}
